import type { MobData } from './types'

// ─── Spherical spatial hash using theta/phi grid ────────────────────
// Enables O(1) neighbor lookups for flocking on a sphere.

export class SphericalSpatialHash {
  private cells = new Map<number, number[]>()
  private cellSize: number  // angular cell size in radians

  constructor(cellSizeRadians: number = 0.01) {  // ~100 units at R=10000
    this.cellSize = cellSizeRadians
  }

  private key(nx: number, ny: number, nz: number): number {
    const phi = Math.acos(Math.min(1, Math.max(-1, ny)))   // [0, PI]
    const theta = Math.atan2(nz, nx) + Math.PI              // [0, 2PI]
    const ci = Math.floor(phi / this.cellSize)
    const cj = Math.floor(theta / this.cellSize)
    return ci * 10000 + cj
  }

  rebuild(mobs: MobData[]) {
    this.cells.clear()
    for (let i = 0; i < mobs.length; i++) {
      const m = mobs[i]
      const k = this.key(m.nx, m.ny, m.nz)
      let cell = this.cells.get(k)
      if (!cell) {
        cell = []
        this.cells.set(k, cell)
      }
      cell.push(i)
    }
  }

  getNeighborIndices(nx: number, ny: number, nz: number): number[] {
    const phi = Math.acos(Math.min(1, Math.max(-1, ny)))
    const theta = Math.atan2(nz, nx) + Math.PI
    const ci = Math.floor(phi / this.cellSize)
    const cj = Math.floor(theta / this.cellSize)

    const result: number[] = []
    for (let di = -1; di <= 1; di++) {
      for (let dj = -1; dj <= 1; dj++) {
        const cell = this.cells.get((ci + di) * 10000 + (cj + dj))
        if (cell) {
          for (let k = 0; k < cell.length; k++) result.push(cell[k])
        }
      }
    }
    return result
  }
}
